<template>
  <div :class="`v-datepicker pillars-col-${openPillars}`">
    <HTextfield
      icon="icCalendar"
      placeholder="00/00/0000"
      :label="label"
      :value="formattedValue"
      @update:value="onInput"
      @click="toggleDatePicker"
    />
    <div
      :class="{
        'datepicker__content--border': !hasNavigation,
        datepicker__content: true
      }"
      :data-show="showDatepicher"
    >
      <ul v-if="hasNavigation" class="datepicker__list">
        <WParagraph class="datepicker__list-title">
          {{ navigationTitle }}
        </WParagraph>
        <li v-for="(date, index) in navigationList" :key="index">
          <button
            type="button"
            transparent
            @click.stop="onNavigationClick(date.value)"
          >
            {{ date.label }}
          </button>
        </li>
      </ul>

      <DatePicker
        ref="picker"
        locale="pt-BR"
        :min-date="minDate"
        :max-date="maxDate"
        :model-value="value"
        @dayclick="onDayClick"
      />
    </div>
  </div>
</template>

<script>
import 'v-calendar/style.css'

import { WParagraph } from '@alice-health/wonderland-vue'
import add from 'date-fns/add'
import addMinutes from 'date-fns/addMinutes'
import format from 'date-fns/format'
import formatISO from 'date-fns/formatISO'
import { DatePicker } from 'v-calendar'
import { mapGetters } from 'vuex'

import { HTextfield } from '../atoms'

export default {
  components: {
    DatePicker,
    HTextfield,
    WParagraph
  },
  props: {
    id: { type: String, default: undefined },
    label: { type: String, default: undefined },
    minDate: Date,
    maxDate: {
      type: Date,
      default() {
        return new Date()
      }
    },
    value: Date,
    hasNavigation: Boolean,
    navigationTitle: {
      type: String,
      default: 'AVANÇAR'
    },
    navigationList: {
      type: Array,
      default() {
        return [
          {
            label: '1 semana',
            value: add(Date.now(), { weeks: 1 })
          },
          {
            label: '1 mês',
            value: add(Date.now(), { months: 1 })
          },
          {
            label: '2 meses',
            value: add(Date.now(), { months: 2 })
          },
          {
            label: '3 meses',
            value: add(Date.now(), { months: 3 })
          },
          {
            label: '6 meses',
            value: add(Date.now(), { months: 6 })
          },
          {
            label: '1 ano',
            value: add(Date.now(), { years: 1 })
          }
        ]
      }
    }
  },
  emits: ['day:clicked', 'clear'],
  data() {
    return {
      isDatePickerOpen: false
    }
  },
  computed: {
    ...mapGetters('pillars', ['openPillars']),
    formattedValue() {
      if (this.value) {
        return format(
          addMinutes(this.value, this.value.getTimezoneOffset()),
          'dd/MM/yyyy'
        )
      }
      return 'dd/mm/aaaa'
    },
    showDatepicher() {
      return this.isDatePickerOpen || null
    }
  },
  watch: {
    value(date) {
      if (date) {
        this.$refs.picker.move(date)
      }
    }
  },
  methods: {
    toggleDatePicker() {
      this.isDatePickerOpen = !this.isDatePickerOpen
      if (this.value) {
        this.$refs.picker.move(this.value)
      }
    },
    onDayClick(event) {
      if (event.isDisabled) return
      this.close()
      this.$emit('day:clicked', event)
    },
    onNavigationClick(date) {
      const id = formatISO(date, { representation: 'date' })
      this.onDayClick({ date, id })
    },
    close() {
      this.isDatePickerOpen = false
    },
    onInput(value) {
      if (!value) {
        this.$emit('clear', { date: null, id: null })
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/style/_non-rendering';
.v-datepicker {
  position: relative;

  .datepicker {
    &__content {
      background-color: var(--sys-color-surface-background-light);
      border: var(--gl-border-width-xs) solid var(--gl-color-shades-gray-20);
      border-radius: var(--gl-border-radius-sm);
      box-shadow: var(--gl-shadow-level-01);
      display: none;
      justify-content: space-around;
      left: 0;
      margin: var(--gl-border-radius-sm) auto 0;
      max-width: 440px;
      position: absolute;
      width: 100%;
      z-index: 9;

      &--border {
        left: auto;
        right: 0;
        overflow: hidden;
        width: auto;
      }

      &[data-show] {
        display: flex;
        flex-wrap: wrap;
      }
    }

    &__list {
      border-right: var(--gl-border-width-xs) solid
        var(--gl-color-shades-gray-20);
      flex-grow: 1;
      margin: 0;
      padding: var(--gl-spacing-04) var(--gl-spacing-02);

      li {
        display: inline-block;
        list-style: none;
      }

      button {
        padding-top: var(--gl-spacing-01);
        padding-bottom: var(--gl-spacing-01);
      }

      &-title {
        @extend %caption;
        color: var(--sys-color-content-secondary);
        display: block;
        font-size: var(--gl-font-size-xxxs);
        letter-spacing: var(--gl-letter-spacing-rule-03);
        margin: var(--gl-spacing-02);
      }

      @media (min-width: 1536px) {
        li {
          display: block;
        }
      }
    }
  }

  &.pillars-col {
    &-3 {
      .datepicker__list {
        border: none;
        max-width: 100%;

        li {
          display: inline-block;
          margin-right: var(--gl-spacing-03);
        }
      }
    }
  }

  .v-container {
    border: none;
    flex-grow: 1;
    padding: var(--gl-spacing-03);
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .v-highlight {
    background-color: var(--gl-color-shades-gray-100) !important;
  }

  .v-header {
    padding: 10px 18px 16px;
  }

  .v-title {
    font-family: var(--gl-font-family-base);
    font-size: var(--text-xs) !important;
    font-weight: var(--gl-font-weight-regular) !important;
    letter-spacing: var(--gl-letter-spacing-rule-03);
    text-transform: uppercase;
  }

  .v-day {
    min-height: 0;
    &-content {
      font-family: var(--gl-font-family-base);
      font-size: var(--gl-font-size-xxxs);
    }
  }
}
</style>
